/**
 * Module-level store for `useAlerts`' optimistic-dismiss set (THE-995).
 *
 * `useAlerts()` mounts independently at three places in the same tab —
 * `useHubYouTabDot` (the dot), `HubAccountMenu` (desktop list),
 * `YouPage` (mobile list) — each with its own `useState` and its own
 * Firestore listener. A dismiss click lands on exactly one of those
 * mounts, but Firestore's `dismissedAt` write + snapshot round-trip takes a
 * beat; during that window the other mounts' local state didn't know the
 * click happened, so the You-tab dot could clear before (or after) the list
 * it points at — same underlying alert, two different answers for one frame.
 *
 * Hoisting the set to a plain module-level store (not React context — these
 * three mounts live in disjoint parts of the tree with no shared provider
 * above them) makes every `useAlerts()` call read the one set through
 * `useSyncExternalStore`, so a dismiss is visible to all of them the instant
 * it happens. Same pattern as `consentStore.ts`.
 */

type Listener = () => void;

const listeners = new Set<Listener>();
let dismissed = new Set<string>();

function setDismissed(next: Set<string>): void {
  dismissed = next;
  listeners.forEach((listener) => listener());
}

/** Current snapshot. Reference-stable until something changes. */
export function getDismissedAlertIds(): Set<string> {
  return dismissed;
}

/** Subscribe to changes. Returns an unsubscribe function. */
export function subscribeDismissedAlertIds(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The × was clicked on whichever mount handled it. */
export function markAlertDismissed(alertId: string): void {
  if (dismissed.has(alertId)) return;
  const next = new Set(dismissed);
  next.add(alertId);
  setDismissed(next);
}

/** The callable rejected — roll back so the alert reappears everywhere. */
export function unmarkAlertDismissed(alertId: string): void {
  if (!dismissed.has(alertId)) return;
  const next = new Set(dismissed);
  next.delete(alertId);
  setDismissed(next);
}

/**
 * Drop pending ids once the BE has caught up (the dismissed doc fell out of
 * `presentIds`). Any ONE mount's snapshot confirming this is sufficient —
 * every mount queries the same uid/collection/filter, so they converge to
 * the same result within one round-trip of each other.
 */
export function reconcileDismissedAlertIds(presentIds: ReadonlySet<string>): void {
  if (dismissed.size === 0) return;
  const next = new Set<string>();
  Array.from(dismissed).forEach((id) => {
    if (presentIds.has(id)) next.add(id);
  });
  if (next.size !== dismissed.size) setDismissed(next);
}

/** New uid (sign-in, sign-out, account switch) — nothing pending carries over. */
export function resetDismissedAlertIds(): void {
  if (dismissed.size === 0) return;
  setDismissed(new Set());
}

/** Test-only: drop all state so the next read starts clean. */
export function __resetAlertDismissStoreForTests(): void {
  dismissed = new Set();
  listeners.clear();
}
